// 구인 풀 관리 페이지
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Stack,
  Textarea,
  Checkbox,
  CheckboxGroup
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import { getCandidatePool, saveCandidate } from 'utils/firestoreUtils';
import { sendSignUpInvitationSMS } from 'utils/smsUtils';
import { useAuth } from 'contexts/AuthContext';

function JobPool() {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  
  // userType 목록 (고정값)
  const userTypes = ['학생', '기타'];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    userType: '학생',
    preferredJobs: [],
    availableDays: [],
    notes: '',
    status: 'active'
  });
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { userProfile } = useAuth();
  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    loadCandidates();
  }, []);

  useEffect(() => {
    filterCandidates();
  }, [candidates, searchTerm, userTypeFilter, statusFilter]);

  async function loadCandidates() {
    try {
      setLoading(true);
      const data = await getCandidatePool();
      setCandidates(data);
    } catch (error) {
      console.error('구인 풀 로딩 오류:', error);
      toast({
        title: '오류',
        description: '구인 풀을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }

  function filterCandidates() {
    let filtered = [...candidates];

    // 검색어 필터
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.includes(searchTerm) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 사용자 유형 필터
    if (userTypeFilter !== 'all') {
      filtered = filtered.filter(c => c.userType === userTypeFilter);
    }

    // 상태 필터
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    setFilteredCandidates(filtered);
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'gray';
      case 'blacklisted': return 'red';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return '활동중';
      case 'inactive': return '휴면';
      case 'blacklisted': return '계정정지';
      default: return status;
    }
  };

  function handleOpenModal(candidate = null) {
    if (candidate) {
      // 수정 모드
      setSelectedCandidate(candidate);
      setFormData({
        name: candidate.name || '',
        email: candidate.email || '',
        phone: candidate.phone || '',
        userType: candidate.userType || userTypes[0],
        preferredJobs: candidate.preferredJobs || [],
        availableDays: candidate.availableDays || [],
        notes: candidate.notes || '',
        status: candidate.status || 'active'
      });
    } else {
      // 추가 모드
      setSelectedCandidate(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        userType: userTypes[0],
        preferredJobs: [],
        availableDays: [],
        notes: '',
        status: 'active'
      });
    }
    onOpen();
  }

  function handleCloseModal() {
    setSelectedCandidate(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      userType: userTypes[0],
      preferredJobs: [],
      availableDays: [],
      notes: '',
      status: 'active'
    });
    onClose();
  }

  // 관리자가 아니면 접근 불가
  if (userProfile.role !== 'admin' && userProfile.role !== 'manager') {
    return (
      <Flex direction="column" pt={{ base: '120px', md: '75px' }} align="center">
        <Text>접근 권한이 없습니다.</Text>
      </Flex>
    );
  }

  
  async function handleSave() {
    try {
      if (!formData.name || !formData.phone) {
        toast({
          title: '필수 항목 누락',
          description: '이름과 연락처는 필수 항목입니다.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const isNewCandidate = !selectedCandidate;
      await saveCandidate(selectedCandidate?.id, formData);

      // 신규 추가인 경우 회원가입 안내 SMS 발송
      if (isNewCandidate) {
        try {
          const smsResult = await sendSignUpInvitationSMS(formData.phone, formData.name);
          
          if (smsResult.success) {
            toast({
              title: '추가 완료',
              description: `${formData.name}님이 추가되었고 회원가입 안내 문자가 발송되었습니다.`,
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
          } else {
            toast({
              title: '추가 완료 (SMS 발송 실패)',
              description: `인원은 추가되었으나 문자 발송에 실패했습니다: ${smsResult.error}`,
              status: 'warning',
              duration: 5000,
              isClosable: true,
            });
          }
        } catch (smsError) {
          console.error('SMS 발송 오류:', smsError);
          toast({
            title: '추가 완료 (SMS 발송 실패)',
            description: '인원은 추가되었으나 문자 발송에 실패했습니다.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: '수정 완료',
          description: '인원이 수정되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      handleCloseModal();
      loadCandidates();
    } catch (error) {
      console.error('저장 오류:', error);
      toast({
        title: '오류',
        description: '저장에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'manager';

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 헤더 영역 */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={textColor} mb={1}>
            구인 풀 관리
          </Heading>
          <Text fontSize="sm" color="gray.500">
            전체 {candidates.length}명 | 조회 {filteredCandidates.length}명
          </Text>
        </Box>
        {isAdmin && (
          <Button colorScheme="blue" onClick={() => handleOpenModal()}>
            인원 추가
          </Button>
        )}
      </Flex>

      {/* 필터 영역 */}
      <Card mb={6}>
        <CardBody py={4}>
          <Grid
            templateColumns={{ sm: '1fr', md: 'repeat(3, 1fr)' }}
            gap={4}
          >
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="이름, 전화번호, 이메일 검색"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
            >
              <option value="all">전체 유형</option>
              {userTypes.map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </Select>

            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">전체 상태</option>
              <option value="active">활동중</option>
              <option value="inactive">휴면</option>
              <option value="blacklisted">계정정지</option>
            </Select>
          </Grid>
        </CardBody>
      </Card>

      {/* 테이블 */}
      <Card overflowX={{ sm: 'scroll', xl: 'hidden' }}>
        <CardBody>
          <Box overflowX="auto">
            <Table variant="simple" color={textColor}>
              <Thead>
                <Tr>
                  <Th borderColor={borderColor}>이름</Th>
                  <Th borderColor={borderColor}>연락처</Th>
                  <Th borderColor={borderColor}>유형</Th>
                  <Th borderColor={borderColor}>희망업무</Th>
                  <Th borderColor={borderColor}>가능요일</Th>
                  <Th borderColor={borderColor}>상태</Th>
                  <Th borderColor={borderColor}>관리</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={12}>
                      로딩 중...
                    </Td>
                  </Tr>
                ) : filteredCandidates.length === 0 ? (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={12}>
                      <Text fontSize="lg" color="gray.500">
                        조건에 맞는 인원이 없습니다
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  filteredCandidates.map((candidate) => (
                    <Tr key={candidate.id}>
                      <Td borderColor={borderColor}>{candidate.name}</Td>
                      <Td borderColor={borderColor}>{candidate.phone}</Td>
                      <Td borderColor={borderColor}>{candidate.userType}</Td>
                      <Td borderColor={borderColor}>
                        {candidate.preferredJobs?.join(', ') || '-'}
                      </Td>
                      <Td borderColor={borderColor}>
                        {candidate.availableDays?.join(', ') || '-'}
                      </Td>
                      <Td borderColor={borderColor}>
                        <Badge colorScheme={getStatusColor(candidate.status)}>
                          {getStatusText(candidate.status)}
                        </Badge>
                      </Td>
                      <Td borderColor={borderColor}>
                        <Button 
                          size="sm" 
                          colorScheme="blue" 
                          variant="outline"
                          onClick={() => handleOpenModal(candidate)}
                        >
                          수정
                        </Button>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
      </Card>

      {/* 인원 추가/수정 모달 */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedCandidate ? '인원 정보 수정' : '인원 추가'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>이름</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="이름 입력"
                />
              </FormControl>

              <FormControl>
                <FormLabel>이메일</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="example@email.com"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>연락처</FormLabel>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="010-1234-5678"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>유형</FormLabel>
                <Select
                  value={formData.userType}
                  onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                >
                  {userTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>희망 업무</FormLabel>
                <CheckboxGroup
                  value={formData.preferredJobs}
                  onChange={(values) => setFormData({ ...formData, preferredJobs: values })}
                >
                  <Stack spacing={2}>
                    <Checkbox value="카페(운죽정)">카페(운죽정)</Checkbox>
                    <Checkbox value="편의점">편의점</Checkbox>
                    <Checkbox value="식당">식당</Checkbox>
                    <Checkbox value="행사">행사</Checkbox>
                    <Checkbox value="사무">사무</Checkbox>
                  </Stack>
                </CheckboxGroup>
              </FormControl>

              <FormControl>
                <FormLabel>가능 요일</FormLabel>
                <CheckboxGroup
                  value={formData.availableDays}
                  onChange={(values) => setFormData({ ...formData, availableDays: values })}
                >
                  <Stack spacing={2} direction="row" flexWrap="wrap">
                    <Checkbox value="월">월</Checkbox>
                    <Checkbox value="화">화</Checkbox>
                    <Checkbox value="수">수</Checkbox>
                    <Checkbox value="목">목</Checkbox>
                    <Checkbox value="금">금</Checkbox>
                    <Checkbox value="토">토</Checkbox>
                    <Checkbox value="일">일</Checkbox>
                  </Stack>
                </CheckboxGroup>
              </FormControl>

              <FormControl>
                <FormLabel>메모</FormLabel>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="추가 정보나 특이사항을 입력하세요"
                  minH="100px"
                />
              </FormControl>

              <FormControl>
                <FormLabel>상태</FormLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="active">활동중</option>
                  <option value="inactive">휴면</option>
                  <option value="blacklisted">계정정지</option>
                </Select>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal}>
              취소
            </Button>
            <Button colorScheme="blue" onClick={handleSave}>
              {selectedCandidate ? '수정' : '추가'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default JobPool;

